import { forkJoin, Observable } from 'rxjs';

import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { CONTRIBUTION_TYPE } from '../../constants/fund/contribution-type';
import {
  DownloadActivityRequest,
  DownloadActivityResponse,
  PendingTransactionItem,
  PendingTransactionRequest,
} from '../../models/fund/activity.model';
import { GetCountriesResponse } from '../../models/fund/admin';
import { CurrencySet } from '../../models/fund/contribute-data';
import { BalanceCurrency } from '../../models/fund/transaction.model';
import { ApiService } from './api.service';

/**
 * Request payload for getting completed transactions
 */
export interface GetCompletedTransactionsRequest {
  cseg5: string;
  startDateInMillis: string;
  endDateInMillis: string;
  currency: string;
  assetInternalId: string | null;
  transactions: boolean;
  type: string;
  dateFormat: string | null;
  order: 'DESC' | 'ASC';
  sort: string;
}

/**
 * Completed transaction response from API
 */
export interface CompletedTransaction {
  internalId: string;
  description: string;
  location: string;
  amount: number;
  currency: string;
  submittedOnString: string;
  completedOnString: string;
  tabType: string;
  statementNote: string | null;
  recurrencePattern: string | null;
  totalTerms: number;
  currentTerm: number;
  submittedOn: number;
  completedOn: number;
  formattedAmount: string | null;
  recurringTxnId: string | null;
  recurringTxnPattern: string | null;
  recurringTxnStatus: string | null;
  recurringPayment: boolean;
}

/**
 * Response from get-activity-completed API endpoint
 */
export interface GetCompletedTransactionsResponse {
  transactions: CompletedTransaction[];
}

/**
 * Contribution configuration structure
 * Contains detailed configuration for tax-deductible and non-tax-deductible contributions
 */
export interface ContributionConfig {
  logoUrl: string;
  redirectUrl: string | null;
  currencyUuid: string;
  taxDeductible: boolean;
  successMessage: string;
  minimumContribution: number;
  enableDafContributions: boolean;
  postContributionAction: string;
  enableRecurringContributions: boolean;
  taxDeductibleContributionTypes: CONTRIBUTION_TYPE[];
  taxDeductibleRequirementsMessage: string;
  nonTaxDeductibleContributionTypes: CONTRIBUTION_TYPE[];
  taxDeductibleContributionMinimumAmountRequired: boolean;
}

/**
 * Network member configuration
 */
export interface NetworkMemberConfiguration {
  name: string | null;
  logoUrl: string | null;
  description: string | null;
  taxDeductionFundName: string | null;
  withRecurring: boolean;
  config: any | null;
  contriConfig: ContributionConfig;
}

/**
 * Response from get-country-details API endpoint
 */
export interface GetCountryDetailsResponse {
  uuid: string;
  name: string;
  taxDeduction: boolean;
  partOfEU: boolean;
  mainCurrency: string | null;
  currencySet: CurrencySet[];
  networkMember: NetworkMemberConfiguration;
}

/**
 * Request payload for searching charities
 */
export interface SearchCharityRequest {
  countryName: string;
  charityNameOrNumber: string;
  taxDeduction: boolean;
}

/**
 * Charity search result from API
 */
export interface SearchCharityResult {
  cseg5: string;
  fundName: string;
  fundNumber: string;
  fundType: string;
  status: string;
  entityName: string | null;
}

export interface FundBalance {
  availableAmount: number;
  closingBalance: number;
  currency: string;
  currencySymbol: string;
  primary: boolean;
}

/**
 * Response from the generate-donate-link API endpoint
 */
export interface GenerateDonateLinkResponse {
  generatedLink: string;
  donateButtonName: string | null;
  donateButtonDescription: string | null;
  publicVisible: boolean;
}

export interface FxConversionPayload {
  cseg5: string;
  fundName: string;
  date: number;
  currencyFrom: string;
  currencyTo: string;
  amountFrom: number | 'F';
  amountTo: number | 'F';
  statementNode: string;
}

@Injectable({
  providedIn: 'root',
})
export class TransactionApiService {
  private readonly resourceUrl = '/transaction';

  constructor(private apiService: ApiService) {}

  getBalanceCurrencies(cseg5: string): Observable<BalanceCurrency[]> {
    return this.apiService.get(
      `${this.resourceUrl}/get-balance-currencies?cseg5=${cseg5}`
    ) as unknown as Observable<BalanceCurrency[]>;
  }

  getCompletedTransactions(
    body: GetCompletedTransactionsRequest
  ): Observable<GetCompletedTransactionsResponse> {
    return this.apiService.post(
      `${this.resourceUrl}/get-activity-completed`,
      body
    ) as Observable<GetCompletedTransactionsResponse>;
  }

  /**
   * Search for countries by name
   * @param term - Search term (country name prefix). Empty string returns all countries.
   * @returns Observable of GetCountriesResponse containing matching countries
   */
  getCountriesByString(term: string): Observable<GetCountriesResponse> {
    const params = new HttpParams().set('country', term || '');
    return this.apiService.get(
      this.resourceUrl + '/get-country',
      params
    ) as Observable<GetCountriesResponse>;
  }

  getCountryDetails(countryUuid: string): Observable<GetCountryDetailsResponse> {
    return this.apiService.post(this.resourceUrl + '/get-country-details', {
      countryUuid,
    }) as Observable<GetCountryDetailsResponse>;
  }

  /**
   * Search for charities by name or number
   * @param request - Search request containing country UUID, search term, and tax deduction preference
   * @returns Observable of SearchCharityResult array containing matching charities
   */
  searchCharity(request: SearchCharityRequest): Observable<SearchCharityResult[]> {
    return this.apiService.post(
      this.resourceUrl + '/search-charity',
      request
    ) as Observable<SearchCharityResult[]>;
  }

  getActivityPending(
    body: PendingTransactionRequest
  ): Observable<PendingTransactionItem[]> {
    return this.apiService.post(
      `${this.resourceUrl}/get-activity-pending`,
      body
    ) as Observable<PendingTransactionItem[]>;
  }

  getActivityAndCurrencies(
    body: GetCompletedTransactionsRequest,
    cseg5: string,
    pendingBody: PendingTransactionRequest
  ): Observable<{
    currencies: BalanceCurrency[];
    completed: GetCompletedTransactionsResponse;
    pending: PendingTransactionItem[];
  }> {
    return forkJoin({
      currencies: this.getBalanceCurrencies(cseg5),
      completed: this.getCompletedTransactions(body),
      pending: this.getActivityPending(pendingBody),
    });
  }

  getActivityCompletedAndPending(
    body: GetCompletedTransactionsRequest,
    pendingBody: PendingTransactionRequest
  ): Observable<{
    completed: GetCompletedTransactionsResponse;
    pending: PendingTransactionItem[];
  }> {
    return forkJoin({
      completed: this.getCompletedTransactions(body),
      pending: this.getActivityPending(pendingBody),
    });
  }

  downloadActivityCompleted(
    body: DownloadActivityRequest
  ): Observable<DownloadActivityResponse> {
    return this.apiService.post(
      `${this.resourceUrl}/download-activity-completed`,
      body
    ) as Observable<DownloadActivityResponse>;
  }

  getFundBalances(fundId: string): Observable<FundBalance[]> {
    const params = new HttpParams().set('cseg5', fundId);
    return this.apiService.get(
      this.resourceUrl + '/get-fund-balances',
      params
    ) as Observable<FundBalance[]>;
  }

  calculateFx(body: FxConversionPayload): Observable<number> {
    return this.apiService.post(
      `${this.resourceUrl}/calculate-fx`,
      body
    ) as Observable<number>;
  }

  createFx(body: FxConversionPayload): Observable<any> {
    return this.apiService.post(
      `${this.resourceUrl}/create-fx`,
      body
    ) as Observable<any>;
  }

  checkMinimumAmount(
    currencySymbol: string
  ): Observable<number> {
    return this.apiService.get(
      `${this.resourceUrl}/calculate-minimum-amount/${currencySymbol}`
    ) as Observable<number>;
  }

  /**
   * Generates the public donate (giving) link for a fund — used by the Donate
   * Buttons feature for both the embeddable HTML button and the standalone
   * giving link, regardless of fund type (Foundation or Charity).
   * @param fundNumberInternalId - Internal fund id (cseg5/fundNumber)
   */
  generateDonateLink(fundNumberInternalId: string): Observable<GenerateDonateLinkResponse> {
    const params = new HttpParams().set('fundNumberInternalId', fundNumberInternalId);
    return this.apiService.get(
      `${this.resourceUrl}/generate-donate-link`,
      params
    ) as Observable<GenerateDonateLinkResponse>;
  }
}
